// Classroom Form Component - Reusable for Create and Edit
// Follows Single Responsibility Principle and Component Composition Pattern

import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col
} from 'reactstrap';
import { IClassroom, IUser, IProgram, ICustomCriterion, IModule } from '../../../models';
import ModuleManager from './ModuleManager';
import { generateDefaultModules, validateModules } from '../../../utils/moduleUtils';

// Props Interface following Interface Segregation Principle
interface ClassroomFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: ClassroomFormData) => Promise<void>;
  classroom?: IClassroom | null; // null for create, IClassroom for edit
  initialData?: IClassroom | null; // for duplication
  program: IProgram | null;
  programClassrooms: IClassroom[];
  teachers: IUser[];
}

// Form Data Interface - separated from IClassroom for form handling
export interface ClassroomFormData {
  programPosition: number;
  name: string;
  subject: string;
  description: string;
  accreditation?: string;
  teacherId: string;
  isActive: boolean;
  materialPrice: number;
  room: string;
  schedule: {
    dayOfWeek: string;
    time: string;
    duration: number;
  };
  evaluationCriteria: {
    questionnaires: number;
    attendance: number;
    participation: number;
    participationPointsPerModule: number;
    finalExam: number;
    customCriteria: ICustomCriterion[];
    participationRecords: any[];
  };
  modules: IModule[];
}

// Initial form state factory - Factory Pattern
const createInitialFormState = (
  classroom?: IClassroom | null,
  initialData?: IClassroom | null,
  programClassrooms: IClassroom[] = []
): ClassroomFormData => {
  const sourceData = classroom || initialData;
  const defaultProgramPosition = classroom
    ? classroom.programPosition || 1
    : programClassrooms.length + 1;

  if (sourceData) {
    return {
      programPosition: classroom ? (sourceData.programPosition || 1) : defaultProgramPosition,
      name: classroom ? sourceData.name : `${sourceData.name} (Copia)`,
      subject: sourceData.subject,
      accreditation: sourceData.accreditation || '',
      description: sourceData.description || '',
      teacherId: sourceData.teacherId,
      isActive: true, // Always active for new/duplicated classes
      materialPrice: sourceData.materialPrice || 0,
      room: sourceData.room || '',
      schedule: {
        dayOfWeek: sourceData.schedule?.dayOfWeek || 'Monday',
        time: sourceData.schedule?.time || '18:00',
        duration: sourceData.schedule?.duration || 120
      },
      evaluationCriteria: {
        questionnaires: sourceData.evaluationCriteria?.questionnaires || 25,
        attendance: sourceData.evaluationCriteria?.attendance || 25,
        participation: sourceData.evaluationCriteria?.participation || 25,
        participationPointsPerModule: sourceData.evaluationCriteria?.participationPointsPerModule || 1,
        finalExam: sourceData.evaluationCriteria?.finalExam || 25,
        customCriteria: sourceData.evaluationCriteria?.customCriteria || [],
        participationRecords: [] // Reset records for new/duplicated classes
      },
      modules: classroom 
        ? sourceData.modules || generateDefaultModules(8)
        : generateDefaultModules(8) // Fresh modules for duplication
    };
  }

  // Default values for new classroom
  return {
    programPosition: defaultProgramPosition,
    name: '',
    subject: '',
      accreditation: '',
    description: '',
    teacherId: '',
    isActive: true,
    materialPrice: 0,
    room: '',
    schedule: {
      dayOfWeek: 'Monday',
      time: '18:00',
      duration: 120
    },
    evaluationCriteria: {
      questionnaires: 25,
      attendance: 25,
      participation: 25,
      participationPointsPerModule: 1,
      finalExam: 25,
      customCriteria: [] as ICustomCriterion[],
      participationRecords: []
    },
    modules: generateDefaultModules(8)
  };
};

const ClassroomForm: React.FC<ClassroomFormProps> = ({
  isOpen,
  onClose,
  onSave,
  classroom,
  initialData,
  program,
  programClassrooms,
  teachers
}) => {
  const [formData, setFormData] = React.useState<ClassroomFormData>(
    () => createInitialFormState(classroom, initialData, programClassrooms)
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Update form when classroom prop changes - Reactive Pattern
  React.useEffect(() => {
    if (isOpen) {
      setFormData(createInitialFormState(classroom, initialData, programClassrooms));
    }
  }, [classroom, initialData, isOpen, programClassrooms]);

  // Form field update handler - reduces boilerplate
  const updateField = (field: keyof ClassroomFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateScheduleField = (field: keyof ClassroomFormData['schedule'], value: any) => {
    setFormData(prev => ({
      ...prev,
      schedule: { ...prev.schedule, [field]: value }
    }));
  };

  const updateEvaluationField = (
    field: keyof ClassroomFormData['evaluationCriteria'],
    value: any
  ) => {
    setFormData(prev => ({
      ...prev,
      evaluationCriteria: { ...prev.evaluationCriteria, [field]: value }
    }));
  };

  // Update modules array
  const updateModules = (modules: IModule[]) => {
    setFormData(prev => ({ ...prev, modules }));
  };

  // Validation - Business Logic
  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'El nombre de la clase es requerido';
    if (!formData.subject.trim()) return 'La materia es requerida';
    if (!formData.teacherId) return 'Debe seleccionar un profesor';
    if (formData.programPosition < 1) return 'Debe seleccionar una posición válida dentro del programa';

    const totalPoints =
      formData.evaluationCriteria.questionnaires +
      formData.evaluationCriteria.attendance +
      formData.evaluationCriteria.participation +
      formData.evaluationCriteria.finalExam;

    if (totalPoints !== 100) {
      return 'Los criterios de evaluación deben sumar exactamente 100 puntos';
    }

    // Validate modules
    const modulesValidation = validateModules(formData.modules);
    if (!modulesValidation.valid) {
      return modulesValidation.error || 'Error en la configuración de módulos';
    }

    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      alert(error);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
      handleClose();
    } catch (error) {
      console.error('Error saving classroom:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(createInitialFormState(null, null, []));
    setIsSubmitting(false);
    onClose();
  };

  // Calculate evaluation total
  const evaluationTotal =
    formData.evaluationCriteria.questionnaires +
    formData.evaluationCriteria.attendance +
    formData.evaluationCriteria.participation +
    formData.evaluationCriteria.finalExam;

  const isEditMode = !!classroom;
  const totalAvailablePositions = classroom ? programClassrooms.length : programClassrooms.length + 1;
  const positionOptions = Array.from({ length: Math.max(totalAvailablePositions, 1) }, (_, index) => index + 1);
  const assignableTeachers = React.useMemo(
    () => teachers
      .filter((user) => user.isTeacher === true)
      .sort((left, right) =>
        `${left.firstName} ${left.lastName}`.localeCompare(
          `${right.firstName} ${right.lastName}`,
          'es'
        )
      ),
    [teachers]
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" className="management-modal">
      <ModalHeader toggle={handleClose}>
        {isEditMode ? 'Editar Clase' : `Nueva Clase para ${program?.name || ''}`}
      </ModalHeader>
      <ModalBody>
        <Form>
          <Row>
            {/* Basic Information */}
            <Col md={6}>
              <FormGroup>
                <Label for="className">Nombre de la Clase *</Label>
                <Input
                  type="text"
                  id="className"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Ej: Grupo A"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="subject">Materia *</Label>
                <Input
                  type="text"
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => updateField('subject', e.target.value)}
                  placeholder="Ej: Introducción a la Teología"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="programPosition">Lugar dentro del programa *</Label>
                <Input
                  type="select"
                  id="programPosition"
                  value={formData.programPosition}
                  onChange={(e) => updateField('programPosition', parseInt(e.target.value, 10) || 1)}
                >
                  {positionOptions.map((position) => (
                    <option key={position} value={position}>
                      Lugar {position} de {Math.max(totalAvailablePositions, 1)}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

  <Col md={12}>
              <FormGroup>
                <Label for="subject">Texto de Acreditación</Label>
                <Input
                  type="text"
                  id="accreditation"
                  value={formData.accreditation}
                  onChange={(e) => updateField('accreditation', e.target.value)}
                  placeholder="Ej: Introducción a la Teología"
                />
              </FormGroup>
            </Col>

            {/* Description */}
            <Col md={12}>
              <FormGroup>
                <Label for="classDescription">Descripción</Label>
                <Input
                  type="textarea"
                  id="classDescription"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                />
              </FormGroup>
            </Col>

            {/* Teacher and Room */}
            <Col md={6}>
              <FormGroup>
                <Label for="teacher">Profesor *</Label>
                <Input
                  type="select"
                  id="teacher"
                  value={formData.teacherId}
                  onChange={(e) => updateField('teacherId', e.target.value)}
                >
                  <option value="">Seleccione un profesor</option>
                  {assignableTeachers.map(teacher => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.firstName} {teacher.lastName}
                    </option>
                  ))}
                </Input>
                {assignableTeachers.length === 0 && (
                  <small className="text-warning">
                    No hay usuarios marcados con la propiedad “Es profesor”.
                  </small>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="room">Salón/Aula</Label>
                <Input
                  type="text"
                  id="room"
                  value={formData.room}
                  onChange={(e) => updateField('room', e.target.value)}
                  placeholder="Ej: Aula 101, Salón Principal"
                />
              </FormGroup>
            </Col>

            {/* Material Price */}
            <Col md={12}>
              <FormGroup>
                <Label for="materialPrice">Precio del Material (RD$)</Label>
                <Input
                  type="number"
                  id="materialPrice"
                  value={formData.materialPrice}
                  onChange={(e) => updateField('materialPrice', parseInt(e.target.value) || 0)}
                  min={0}
                />
              </FormGroup>
            </Col>

            {/* Schedule Section */}
            <Col md={12}>
              <hr />
              <h6 className="mb-3">Horario</h6>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label for="dayOfWeek">Día de la Semana</Label>
                <Input
                  type="select"
                  id="dayOfWeek"
                  value={formData.schedule.dayOfWeek}
                  onChange={(e) => updateScheduleField('dayOfWeek', e.target.value)}
                >
                  <option value="Monday">Lunes</option>
                  <option value="Tuesday">Martes</option>
                  <option value="Wednesday">Miércoles</option>
                  <option value="Thursday">Jueves</option>
                  <option value="Friday">Viernes</option>
                  <option value="Saturday">Sábado</option>
                  <option value="Sunday">Domingo</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="time">Hora</Label>
                <Input
                  type="time"
                  id="time"
                  value={formData.schedule.time}
                  onChange={(e) => updateScheduleField('time', e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="duration">Duración (minutos)</Label>
                <Input
                  type="number"
                  id="duration"
                  value={formData.schedule.duration}
                  onChange={(e) => updateScheduleField('duration', parseInt(e.target.value) || 0)}
                  min={15}
                  step={15}
                />
              </FormGroup>
            </Col>

            {/* Evaluation Criteria Section */}
            <Col md={12}>
              <hr />
              <h6 className="mb-3">Criterios de Evaluación (Total: 100 puntos)</h6>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="questionnaires">Cuestionarios</Label>
                <Input
                  type="number"
                  id="questionnaires"
                  value={formData.evaluationCriteria.questionnaires}
                  onChange={(e) => updateEvaluationField('questionnaires', parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="attendance">Asistencia</Label>
                <Input
                  type="number"
                  id="attendance"
                  value={formData.evaluationCriteria.attendance}
                  onChange={(e) => updateEvaluationField('attendance', parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="participation">Participación</Label>
                <Input
                  type="number"
                  id="participation"
                  value={formData.evaluationCriteria.participation}
                  onChange={(e) => updateEvaluationField('participation', parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="finalExam">Examen Final</Label>
                <Input
                  type="number"
                  id="finalExam"
                  value={formData.evaluationCriteria.finalExam}
                  onChange={(e) => updateEvaluationField('finalExam', parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                />
              </FormGroup>
            </Col>

            {/* Evaluation Total Alert */}
            <Col md={12}>
              <Alert color={evaluationTotal === 100 ? 'success' : 'warning'}>
                <i className={`bi bi-${evaluationTotal === 100 ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
                Total: {evaluationTotal} / 100 puntos
                {evaluationTotal !== 100 && (
                  <span className="ms-2">
                    {evaluationTotal > 100 ? '(Excede en ' : '(Faltan '}
                    {Math.abs(100 - evaluationTotal)} puntos)
                  </span>
                )}
              </Alert>
            </Col>

            {/* Modules Section */}
            <Col md={12} className="mt-3">
              <ModuleManager
                modules={formData.modules}
                onChange={updateModules}
              />
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || evaluationTotal !== 100}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Guardando...
            </>
          ) : (
            isEditMode ? 'Actualizar Clase' : 'Crear Clase'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClassroomForm;
